import { useState } from "react";

import { Typography } from "@mui/material";
import { useAlertContext, useConfirmationDialogContext } from "@shared";

import { EdictEntryResponse } from "@/api";
import { writeFailureMessage } from "@/common/components/mail-merge-access/MailMergeAccess.utils";
import { MAIL_MERGE_TEXT_EMPHASIS } from "@/common/components/mail-merge-access/MailMergeTextEmphasis";
import { useMailMergeAccessPrompts } from "@/common/components/mail-merge-access/hooks/useMailMergeAccessPrompts";
import { Trans, useTranslation } from "@/translations";

import { OZUF_DATA_SOURCE_FILE_NAME, buildOzufDataSource, distinctOzufRecipients } from "../Edicts.utils";

/**
 * Writes the OZUF mail merge data source (Serienbrief-Datenquelle) for the selected recipients into the
 * released folder. SONeu is the data source only: the letters themselves are merged in Word.
 *
 * **The caller must render the returned `folderDialog`**, which the folder grant raises when access is not
 * in place yet. `onWritten` runs only once the file is written: after a failure the selection is what the
 * user retries with.
 */
export function useOzufMailMerge(onWritten: () => void) {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const { showError } = useAlertContext();
  const { access, folderDialog, requestAccess } = useMailMergeAccessPrompts();
  const [isWriting, setIsWriting] = useState(false);

  const mailMergeText = (
    i18nKey: "edicts.mailMergeDialog.text" | "edicts.mailMergeResult.text",
    folder: string,
    count: number
  ) => (
    <Typography component="span">
      <Trans
        i18nKey={i18nKey}
        values={{ fileName: OZUF_DATA_SOURCE_FILE_NAME, folder, count }}
        components={MAIL_MERGE_TEXT_EMPHASIS}
      />
    </Typography>
  );

  const confirmWrite = (folder: string, count: number) =>
    new Promise<boolean>((resolve) => {
      showConfirmationDialog({
        dialogTitleText: t("edicts.mailMergeDialog.title"),
        dialogText: mailMergeText("edicts.mailMergeDialog.text", folder, count),
        agreeText: t("edicts.mailMergeDialog.confirm"),
        onClose: resolve,
      });
    });

  // Not showOkDialog: that takes a plain string, and the text carries markup the <Trans> has to resolve.
  const showWritten = (folder: string, count: number) =>
    showConfirmationDialog({
      dialogTitleText: t("edicts.mailMergeResult.title"),
      dialogText: mailMergeText("edicts.mailMergeResult.text", folder, count),
      showDiscardButton: false,
      onClose: () => undefined,
    });

  const createMailMerge = async (selected: readonly EdictEntryResponse[]) => {
    // Counted after merging, so the dialogs name the letters that will go out rather than the rows ticked.
    const recipients = distinctOzufRecipients(selected);

    if (recipients.length === 0) {
      return;
    }

    // Busy before the first await, so a second click cannot start a second flow while this one reads the grant.
    setIsWriting(true);

    try {
      // Awaited before any other request: it may raise a browser prompt, which needs this click's activation.
      if (!(await requestAccess())) {
        return;
      }

      // Read fresh rather than from this render: on the first click after a page load the configured
      // path may only just have arrived, and the dialog is where the user is told where the file goes.
      const { expectedFolderPath } = await access.readCurrentGrant();

      if (expectedFolderPath === null || !(await confirmWrite(expectedFolderPath, recipients.length))) {
        return;
      }

      const result = await access.writeFile(OZUF_DATA_SOURCE_FILE_NAME, buildOzufDataSource(recipients, new Date()));

      if (!result.success) {
        showError(writeFailureMessage(result.failure, t));
        return;
      }

      showWritten(expectedFolderPath, recipients.length);
      onWritten();
    } catch {
      showError(t("mailMerge.access.errors.writeFailed"));
    } finally {
      setIsWriting(false);
    }
  };

  return { folderDialog, createMailMerge, isWriting };
}
